import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";
import { MobileResponsive } from "./MobileResponsive";
import WhiteButton from "../components/WhiteButton";
import TimeInput from "../components/TimeInput";
import { convertToMinutes } from "../utils/convertToMins";
import { useDispatch } from "react-redux";
import useDataFetch from "../hooks/useDataFetch";
import {
  convertTo12Hour,
  convertTo24Hour,
  convertToLocalTime,
} from "../utils/convertTime";
import {
  acceptDriverService,
  rejectDriverService,
  shiftScheduleService,
} from "../features/driverSlice";
import toast from "react-hot-toast";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const ManageDriverDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading, mutate } = useDataFetch(
    `/admin/onboarding-new-drivers-details/${id}`
  );

  const [shiftTimings, setShiftTimings] = useState({
    MON: ["09:00 AM", "11:30 PM"],
    TUE: ["09:00 AM", "11:30 PM"],
    WED: ["09:00 AM", "11:30 PM"],
    THU: ["09:00 AM", "11:30 PM"],
    FRI: ["09:00 AM", "11:30 PM"],
    SAT: ["11:00 AM", "05:30 PM"],
    SUN: ["11:00 AM", "05:30 PM"],
  });

  const [showSidebar, setShowSidebar] = useState(false);
  const [highlightedTimes, setHighlightedTimes] = useState({});
  const [shiftErrors, setShiftErrors] = useState({});
  const [shiftApproved, setShiftApproved] = useState(false);
  const [nonShiftModal, setNonShiftModal] = useState(false);

  const isShiftDriver = data?.data?.is_shift_driver !== false;

  const sidebarRef = useRef(null);

  useEffect(() => {
    if (data && data?.data?.shift_schedule) {
      const updatedTimings = {};

      data?.data?.shift_schedule.forEach((schedule, index) => {
        const dayNames = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

        // Only convert valid times, otherwise assign an empty string
        const startTime =
          schedule?.start_time && schedule?.start_time !== "Invalid date"
            ? convertTo12Hour(schedule?.start_time)
            : "";

        const endTime =
          schedule?.end_time && schedule?.end_time !== "Invalid date"
            ? convertTo12Hour(schedule?.end_time)
            : "";

        // If both start and end times are empty, set empty string for that day
        updatedTimings[dayNames[index]] =
          startTime === "" && endTime === "" ? ["", ""] : [startTime, endTime];
      });

      setShiftTimings(updatedTimings);
    }
  }, [data]);

  const handleEditTime = (day, index, newTime) => {
    setShiftTimings((prevTimings) => {
      const newTimings = { ...prevTimings };

      // Check for "No shift" input
      newTimings[day][index] = newTime === "No shift" ? "" : newTime;

      validateShiftTimings(newTimings, index, day);
      return newTimings;
    });
  };

  const validateShiftTimings = (timings, index, day) => {
    const startTimeInMinutes = convertToMinutes(timings[day][0]);
    const endTimeInMinutes = convertToMinutes(timings[day][1]);
    const newErrors = { ...shiftErrors };

    if (endTimeInMinutes <= startTimeInMinutes && timings[day][1] !== "") {
      newErrors[day] = "End time must be later than start time.";
      setHighlightedTimes((prev) => ({
        ...prev,
        [day]: index,
      }));
    } else if (
      (timings[day][0] === "" && timings[day][1] !== "") ||
      (timings[day][0] !== "" && timings[day][1] === "")
    ) {
      newErrors[day] = "Both start and end times must be filled or both empty.";
      setHighlightedTimes((prev) => ({
        ...prev,
        [day]: index,
      }));
    } else {
      delete newErrors[day]; // Remove error if timing is valid
      setHighlightedTimes((prev) => {
        const { [day]: _, ...rest } = prev;
        return rest;
      });
    }

    setShiftErrors(newErrors);
  };

  const handleApproveShift = async () => {
    if (Object.keys(shiftErrors).length === 0) {
      const resultAction = await dispatch(
        shiftScheduleService({
          driverID: id,
          newSchedule: [
            {
              day: "Monday",
              start_time: shiftTimings.MON[0]
                ? convertTo24Hour(shiftTimings.MON[0])
                : "",
              end_time: shiftTimings.MON[1]
                ? convertTo24Hour(shiftTimings.MON[1])
                : "",
            },
            {
              day: "Tuesday",
              start_time: shiftTimings.TUE[0]
                ? convertTo24Hour(shiftTimings.TUE[0])
                : "",
              end_time: shiftTimings.TUE[1]
                ? convertTo24Hour(shiftTimings.TUE[1])
                : "",
            },
            {
              day: "Wednesday",
              start_time: shiftTimings.WED[0]
                ? convertTo24Hour(shiftTimings.WED[0])
                : "",
              end_time: shiftTimings.WED[1]
                ? convertTo24Hour(shiftTimings.WED[1])
                : "",
            },
            {
              day: "Thursday",
              start_time: shiftTimings.THU[0]
                ? convertTo24Hour(shiftTimings.THU[0])
                : "",
              end_time: shiftTimings.THU[1]
                ? convertTo24Hour(shiftTimings.THU[1])
                : "",
            },
            {
              day: "Friday",
              start_time: shiftTimings.FRI[0]
                ? convertTo24Hour(shiftTimings.FRI[0])
                : "",
              end_time: shiftTimings.FRI[1]
                ? convertTo24Hour(shiftTimings.FRI[1])
                : "",
            },
            {
              day: "Saturday",
              start_time: shiftTimings.SAT[0]
                ? convertTo24Hour(shiftTimings.SAT[0])
                : "",
              end_time: shiftTimings.SAT[1]
                ? convertTo24Hour(shiftTimings.SAT[1])
                : "",
            },
            {
              day: "Sunday",
              start_time: shiftTimings.SUN[0]
                ? convertTo24Hour(shiftTimings.SUN[0])
                : "",
              end_time: shiftTimings.SUN[1]
                ? convertTo24Hour(shiftTimings.SUN[1])
                : "",
            },
          ],
        })
      );

      if (shiftScheduleService.fulfilled.match(resultAction)) {
        toast.success(resultAction.payload.message);
        setShiftApproved(true);
      }
    }
  };

  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const handleCloseSidebar = () => {
    if (Object.keys(shiftErrors).length > 0) {
      toast.error("Please fix the errors before closing the sidebar.");
      return;
    }
    if (isShiftDriver && !shiftApproved) {
      toast.error("Kindly approve the shift first!");
      return;
    }
    setShowSidebar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        handleCloseSidebar();
      }
    };

    if (showSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSidebar, shiftErrors, shiftApproved]);

  const handleAcceptDriver = async () => {
    if (isShiftDriver && !shiftApproved) {
      toast.error("Kindly approve the shift first!");
      return;
    }
    const resultAction = await dispatch(acceptDriverService({ driverID: id }));
    if (acceptDriverService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate("/manage-drivers");
    }
  };

  const handleRejectDriver = async () => {
    const resultAction = await dispatch(
      rejectDriverService({ driverID: id, reason: "Admin rejected the driver." })
    );
    if (rejectDriverService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate("/manage-drivers");
    }
  };

  const renderTable = (sidebar) => (
    <div className="flex flex-col gap-5 py-5 w-full">
      {Object.entries(shiftTimings).map(([day, times], dayIndex) => (
        <div
          key={dayIndex}
          className={`flex justify-between items-center gap-6 w-full px-8 ${
            highlightedTimes[day] !== undefined ? "rounded-md px-4" : "px-4"
          }`}
        >
          <span
            className={`font-bold 2xl:text-base xl:text-base lg:text-sm ${
              highlightedTimes[day] !== undefined
                ? "text-[#E00000]"
                : "text-black"
            }`}
          >
            {day}
          </span>

          <div className="flex justify-end items-center gap-6 2xl:w-[65%] xl:w-[75%] lg:w-[60%] w-auto">
            {times[0] === "" && times[1] === "" && !sidebar ? (
              <div className="flex justify-center items-center gap-6 w-full">
                <span>No shift</span>
              </div>
            ) : (
              <>
                <TimeInput
                  key={`${day}-start`}
                  initialTime={times[0]} // Start time
                  day={day}
                  index={0} // First time input (start time)
                  handleEditTime={handleEditTime}
                  isHighlighted={highlightedTimes[day] === 0}
                  disabled={!sidebar}
                />
                <TimeInput
                  key={`${day}-end`}
                  initialTime={times[1]} // End time
                  day={day}
                  index={1} // Second time input (end time)
                  handleEditTime={handleEditTime}
                  isHighlighted={highlightedTimes[day] === 1}
                  disabled={!sidebar}
                />
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Review and Onboard New Drivers"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {showSidebar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary flex items-start relative">
          <div className="w-full flex items-start">
            <div className="w-[50%]">
              <div className="flex w-full">
                <Link to="/manage-drivers" className="flex items-center gap-3">
                  <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[6px]"
                    />
                  </div>
                  <span className="font-semibold text-base">Back</span>
                </Link>
              </div>

              <div className="py-8 w-full">
                <div className="border-2 border-quaternary w-[75%] px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                  {/* <img
                    src="/assets/user.png"
                    alt={data?.data?.name}
                    className="h-[5.3rem] w-h-[5.3rem] rounded-full"
                  /> */}
                  <Avatar
                    name={data?.data?.name}
                    size="84"
                    round={true}
                    maxInitials={2}
                    textSizeRatio={2.5}
                    style={{ letterSpacing: "1px" }}
                  />
                  <div className="text-center">
                    <h1 className="text-[21px] font-bold tracking-wide capitalize">
                      {data?.data?.name}
                    </h1>
                  </div>
                </div>

                <h2 className="text-[17px] font-semibold pt-3 pb-2">
                  Driver details
                </h2>

                <div className="border-2 border-quaternary w-[75%] px-5 py-1.5 rounded-lg flex flex-col items-center justify-between bg-[#F7F9F8]">
                  {[
                    {
                      label: "Contact number",
                      value: data?.data?.phone,
                    },
                    {
                      label: "Email",
                      value: data?.data?.email,
                    },
                    {
                      label: "Registration No.",
                      value: data?.data?.registration_number,
                    },
                    {
                      label: "Vehicle",
                      value: data?.data?.vehicle_details,
                    },
                    {
                      label: "Vehicle type",
                      value: data?.data?.vehicle_type,
                    },
                    ...(data?.data?.is_shift_driver !== undefined
                      ? [
                          {
                            label: "Shift Driver",
                            value: data?.data?.is_shift_driver ? "Yes" : "No",
                          },
                        ]
                      : []),
                    // {
                    //   label: "Driver's licence",
                    //   value: data?.data?.driver_license,
                    // },
                    // {
                    //   label: "Driver's national insurance",
                    //   value: data?.data?.driver_national_insurance,
                    // },
                    // {
                    //   label: "Vehicle insurance",
                    //   value: data?.data?.vehicle_insurance,
                    // },
                    {
                      label: "Date of birth",
                      value: convertToLocalTime(data?.data?.dob),
                    },
                    {
                      label: "Address",
                      value: data?.data?.address,
                    },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center w-full py-2">
                      <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                        {item.label}
                      </p>
                      <p
                        className={`2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all ${
                          item.label === "Registration No." && "uppercase"
                        }`}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="w-[75%] flex items-center gap-3 py-5">
                  <PrimaryButton
                    text="Accept driver"
                    uppercase={false}
                    mutation={true}
                    width="w-full"
                    event={handleAcceptDriver}
                  />
                  <SecondaryButton
                    text="Reject"
                    uppercase={false}
                    mutation={true}
                    width="w-full"
                    event={handleRejectDriver}
                  />
                </div>
              </div>
            </div>

            <div className="py-12 w-[50%]">
              <div className="flex justify-between items-center 2xl:w-[71%] xl:w-[87%] lg:w-[88%] pt-3 pb-2">
                <h2 className="text-[17px] font-semibold">Shift timings</h2>
              </div>

              {!isShiftDriver ? (
                <div className="border-2 border-quaternary 2xl:w-[71%] xl:w-[87%] lg:w-[88%] rounded-lg flex flex-col items-center justify-center gap-2 bg-[#F7F9F8] py-10 px-6 text-center">
                  <p className="text-gray-500 font-medium">Non-shift driver</p>
                  <p className="text-sm text-gray-400">Shift timings are not applicable for this driver.</p>
                </div>
              ) : (
                <>
                  <div className="border-2 border-quaternary 2xl:w-[71%] xl:w-[87%] lg:w-[88%] rounded-lg flex flex-col items-center justify-between gap-4 bg-[#F7F9F8]">
                    {renderTable()}
                  </div>

                  <div className="2xl:w-[71%] xl:w-[87%] lg:w-[88%] flex flex-col justify-center items-center gap-4 py-5">
                    <WhiteButton
                      text="Review Shift"
                      width="w-[90%]"
                      uppercase={false}
                      event={handleToggleSidebar}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Non-shift driver modal */}
          {nonShiftModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-xl border-t-4 border-quaternary">
                <h2 className="text-lg font-bold text-gray-800 mb-3">Cannot Edit Shift</h2>
                <p className="text-gray-600 mb-6">
                  <strong className="capitalize">{data?.data?.name}</strong> is a non-shift driver. To edit their shift timings, first make them a shift driver.
                </p>
                <button
                  onClick={() => setNonShiftModal(false)}
                  className="w-full bg-quaternary text-white font-semibold py-2.5 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {/* Sidebar */}
          <div
            ref={sidebarRef}
            className={`fixed right-0 top-0 h-full 2xl:w-[40%] xl:w-[45%] lg:w-[50%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              showSidebar ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div
                  onClick={handleCloseSidebar}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold 2xl:text-[25px] xl:text-[24px] lg:text-[23px]">
                    Select timings
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] px-6 rounded-lg flex flex-col items-center justify-between gap-4 bg-[#F7F9F8]">
                {renderTable(true)}
              </div>

              <div className="w-[75%] m-auto flex justify-center py-5">
                <PrimaryButton
                  text="Approve Shift"
                  width="w-full"
                  uppercase={false}
                  event={handleApproveShift}
                  disabled={Object.keys(shiftErrors).length > 0}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageDriverDetail;
